import React from 'react';

import { Button, Flex, Input, Typography } from 'antd';
import { useParams } from 'react-router-dom';
import { Link } from 'react-router-dom';

import { EditBoxContractForm } from './components/EditBoxContractForm';
import { useBoxContract } from './hooks/useBoxContract';
import { useUpdateBoxContract } from './hooks/useUpdateBoxContract';
import FormRow from '@/components/forms/FormRow';
import Loader from '@/components/Loader';
import IdCell from '@/components/tables/IdCell';
import H1 from '@/components/text/H1';
import useError from '@/hooks/useError';

const { Text } = Typography;

export function BoxContractDetailView() {
  const addError = useError();
  const { id } = useParams<{ id: string }>();
  const { result, invalidate } = useBoxContract(id!);
  const updateBoxContract = useUpdateBoxContract();

  if (result.state === 'idle' || result.state === 'loading') {
    return <Loader size={64} />;
  }

  if (result.state === 'error') return <>Error fetching contract {id}.</>;

  const { resource: contract } = result;

  return (
    <Flex vertical style={{ padding: '0 64px', marginBottom: '1rem' }}>
      <H1>Box contract details</H1>
      <Flex vertical style={{ maxWidth: '900px' }}>
        <Flex
          vertical
          align="flex-start"
          gap={24}
          style={{ width: '100%', marginBottom: '2rem' }}
        >
          <FormRow>
            <Flex vertical style={{ width: '50%' }} gap={8} align="flex-start">
              <Text style={{ fontSize: '14px', fontWeight: 500 }}>
                Contract-ID
              </Text>
              <IdCell id={contract.id} />
            </Flex>
            <Flex vertical style={{ width: '50%' }} gap={8} align="flex-start">
              <Text style={{ fontSize: '14px', fontWeight: 500 }}>
                Offer-ID
              </Text>
              <IdCell id={contract.boxContractOffer.id} />
            </Flex>
            <Flex vertical style={{ width: '50%' }} gap={8} align="flex-start">
              <Text style={{ fontSize: '14px', fontWeight: 500 }}>
                PRODUCT-IDs
              </Text>
              <Flex gap={8}>
                {contract.productIDs.map((productID, index) => (
                  <IdCell key={`contract-product${index}`} id={productID} />
                ))}
              </Flex>
            </Flex>
          </FormRow>
          {(contract.partnerId || contract.shopId) && (
            <>
              <Flex style={{ width: '100%' }} gap={40} justify="flex-start">
                {contract.partnerId && (
                  <>
                    <Flex
                      vertical
                      style={{ width: '30%' }}
                      gap={8}
                      align="flex-start"
                    >
                      <Text style={{ fontSize: '14px', fontWeight: 500 }}>
                        Partner-ID
                      </Text>
                      <IdCell id={contract.partnerId} />
                    </Flex>
                    <Link to={`/lp-village/partners/${contract.partnerId}`}>
                      <Button> To Partner</Button>
                    </Link>
                  </>
                )}
                {contract.shopId && (
                  <>
                    <Flex
                      vertical
                      style={{ width: '30%' }}
                      gap={8}
                      align="flex-start"
                    >
                      <Text style={{ fontSize: '14px', fontWeight: 500 }}>
                        Shop-ID
                      </Text>
                      <IdCell id={contract.shopId} />
                    </Flex>
                    <Link to={`/lp-village/shops/${contract.shopId}`}>
                      <Button> To Shop</Button>
                    </Link>
                  </>
                )}
              </Flex>
            </>
          )}

          <FormRow>
            <Flex vertical style={{ width: '50%' }} gap={8} align="flex-start">
              <Text style={{ fontSize: '14px', fontWeight: 500 }}>
                Created At
              </Text>
              <Input
                disabled
                variant="borderless"
                value={contract.createdAt.toLocaleString()}
              />
            </Flex>
            <Flex vertical style={{ width: '50%' }} gap={8} align="flex-start">
              <Text style={{ fontSize: '14px', fontWeight: 500 }}>
                Updated At
              </Text>
              <Input
                disabled
                variant="borderless"
                value={contract.updatedAt.toLocaleString()}
              />
            </Flex>
          </FormRow>
        </Flex>
      </Flex>

      <Flex vertical style={{ maxWidth: '900px' }}>
        <EditBoxContractForm
          contract={contract}
          onSubmit={async (values) => {
            try {
              await updateBoxContract(id!, values);
              invalidate();
            } catch (error) {
              addError(error);
            }
          }}
        />
      </Flex>
    </Flex>
  );
}
